export const WORLD = {
    width: 32,
    height: 18,
    groundY: -6.2,
    buildMinX: 2.6,
    buildMaxX: 13.4,
    buildMinY: -5.85,
    buildMaxY: 5.6,
    launcherX: -12.2,
    launcherY: -4.85,
    flagX: 10.5,
    flagY: -5.0,
    grid: 0.55
}

export const FLAG = {
    width: WORLD.grid * 2,
    height: WORLD.grid * 2
}

export const BUILDER_BUDGET = 60
export const MIN_BLOCKS_TO_PUBLISH = 5

export const DEFAULT_BLOCK_SHAPE = 'rectangle'

export const BLOCK_SHAPES = {
    chip: {
        label: '1x1',
        gridWidth: 1,
        gridHeight: 1,
        width: WORLD.grid,
        height: WORLD.grid,
        healthMultiplier: 0.92,
        compressionMultiplier: 0.82,
        bendingMultiplier: 0.76,
        costMultiplier: 0.42
    },
    halfBrick: {
        label: '2x1',
        gridWidth: 2,
        gridHeight: 1,
        width: WORLD.grid * 2,
        height: WORLD.grid,
        healthMultiplier: 0.96,
        compressionMultiplier: 0.9,
        bendingMultiplier: 0.88,
        costMultiplier: 0.62
    },
    sliver: {
        label: '0.5x2',
        gridWidth: 0.5,
        gridHeight: 2,
        width: WORLD.grid * 0.5,
        height: WORLD.grid * 2,
        healthMultiplier: 0.82,
        compressionMultiplier: 0.58,
        bendingMultiplier: 1.2,
        costMultiplier: 0.42
    },
    square: {
        label: 'Square',
        gridWidth: 2,
        gridHeight: 2,
        width: WORLD.grid * 2,
        height: WORLD.grid * 2,
        healthMultiplier: 1.08,
        compressionMultiplier: 1.18,
        bendingMultiplier: 0.92,
        costMultiplier: 1
    },
    rectangle: {
        label: 'Rectangle',
        gridWidth: 4,
        gridHeight: 1,
        width: WORLD.grid * 4,
        height: WORLD.grid,
        healthMultiplier: 1,
        compressionMultiplier: 0.92,
        bendingMultiplier: 1.04,
        costMultiplier: 1
    },
    beam: {
        label: 'Beam',
        gridWidth: 8,
        gridHeight: 0.5,
        width: WORLD.grid * 8,
        height: WORLD.grid * 0.5,
        healthMultiplier: 0.78,
        compressionMultiplier: 0.72,
        bendingMultiplier: 1.24,
        costMultiplier: 1
    }
}

export const BLOCK_SIZE = {
    width: BLOCK_SHAPES[DEFAULT_BLOCK_SHAPE].width,
    height: BLOCK_SHAPES[DEFAULT_BLOCK_SHAPE].height,
    depth: 0.42,
    area: BLOCK_SHAPES[DEFAULT_BLOCK_SHAPE].width * BLOCK_SHAPES[DEFAULT_BLOCK_SHAPE].height
}

export const MATERIALS = {
    stone: {
        label: 'Stone',
        cost: 3,
        health: 110,
        baseHealth: 110,
        density: 0.0345,
        friction: 0.85,
        frictionAir: 0.002,
        restitution: 0.04,
        toughness: 0.86,
        brittleness: 0.83,
        compressionStrength: 1.25,
        shearStrength: 0.9,
        supportStrength: 1.25,
        shatterThreshold: 0.031,
        fallDamage: 1,
        impactDamping: 0.82,
        breakable: true,
        color: 0x8c96a3,
        accent: '#c1c9d3',
        weakness: 'explosive'
    },
    wood: {
        label: 'Wood',
        cost: 2,
        health: 58,
        baseHealth: 58,
        density: 0.0135,
        friction: 0.8,
        frictionAir: 0.003,
        restitution: 0.17,
        toughness: 0.65,
        brittleness: 1.17,
        compressionStrength: 1.05,
        shearStrength: 0.91,
        supportStrength: 1.02,
        shatterThreshold: 0.021,
        fallDamage: 1,
        impactDamping: 0.67,
        breakable: true,
        color: 0xb5753a,
        accent: '#f1bc76',
        weakness: 'fire'
    },
    glass: {
        label: 'Glass',
        cost: 1,
        health: 24,
        baseHealth: 24,
        density: 0.0115,
        friction: 0.55,
        frictionAir: 0.002,
        restitution: 0.02,
        toughness: 0.42,
        brittleness: 1.49,
        compressionStrength: 0.92,
        shearStrength: 0.79,
        supportStrength: 0.87,
        shatterThreshold: 0.033,
        brittleImpactSpeed: 2.4,
        brittleImpactDamageMultiplier: 0,
        fallDamage: 1.4,
        impactDamping: 0.48,
        breakable: true,
        color: 0x7ed8ee,
        accent: '#b9f2ff',
        weakness: 'iron'
    }
}

export const BOMBS = {
    basic: {
        label: 'Stone Chip',
        shortLabel: 'Chip',
        targetMaterial: 'stone',
        description: 'Light stone bullet for masonry seams.',
        tip: 'Stone Chip is the starter shot. It is light, quick, and best against stone.',
        radius: 0.46,
        density: 0.0475,
        impactStrength: 116,
        impactForce: 0.99,
        materialMultiplier: 1.71,
        offMaterialMultiplier: 1,
        // Tuned with the balance lab so a strong direct hit removes about
        // 80% of a matching block and 20-35% of the others.
        materialMultipliers: {
            stone: 3.5,
            wood: 0.5,
            glass: 0.12
        },
        airResistance: 0.0021,
        groundBounce: 0.18,
        color: 0x5d636c,
        accent: '#d7d7dd',
        soundMaterial: 'stone',
        bonusAgainst: 'stone'
    },
    explosive: {
        label: 'Blast Bomb',
        shortLabel: 'Bomb',
        targetMaterial: 'stone',
        description: 'Explodes on impact and damages nearby supports.',
        tip: 'Blast Bombs break clusters. Aim near joints and crowded supports.',
        radius: 0.43,
        density: 0.0515,
        impactStrength: 123,
        impactForce: 1.35,
        materialMultiplier: 1.75,
        offMaterialMultiplier: 0.94,
        materialMultipliers: {
            stone: 2.22,
            wood: 2.05,
            glass: 2.09
        },
        blastRadius: 1.9,
        blastDamage: 42,
        blastImpulse: 1.6,
        airResistance: 0.0018,
        groundBounce: 0.08,
        color: 0x4f5661,
        accent: '#ffb18e',
        soundMaterial: 'stone',
        bonusAgainst: 'stone'
    },
    fire: {
        label: 'Wood Crusher',
        shortLabel: 'Crusher',
        targetMaterial: 'wood',
        description: 'Midweight bullet that bites into timber.',
        tip: 'Wood Crusher is strongest against wooden supports.',
        radius: 0.53,
        density: 0.025,
        impactStrength: 90,
        impactForce: 1.05,
        materialMultiplier: 1.9,
        offMaterialMultiplier: 0.95,
        materialMultipliers: {
            stone: 1.2,
            wood: 1.5,
            glass: 0.18
        },
        airResistance: 0.0017,
        groundBounce: 0.28,
        color: 0xc9782b,
        accent: '#ffe28f',
        soundMaterial: 'wood',
        bonusAgainst: 'wood'
    },
    iron: {
        label: 'Glass Shatter',
        shortLabel: 'Shatter',
        targetMaterial: 'glass',
        description: 'Bursts mid-flight into glass-cutting shards.',
        tip: 'Glass Shatter bursts halfway into five shards (tap to burst early). Shards cut glass and bounce off stone and wood.',
        radius: 0.4,
        density: 0.034,
        impactStrength: 59,
        impactForce: 0.97,
        materialMultiplier: 1.78,
        offMaterialMultiplier: 0.67,
        materialMultipliers: {
            stone: 0.34,
            wood: 0.78,
            glass: 2.6
        },
        airResistance: 0.0014,
        groundBounce: 0.05,
        // Cluster burst: splits into small shards that only damage glass.
        splitCount: 5,
        splitSpreadDegrees: 24,
        fragmentRadius: 0.17,
        fragmentMaterialMultipliers: {
            stone: 0,
            wood: 0,
            glass: 6
        },
        color: 0xbec3c7,
        accent: '#f2f4f6',
        soundMaterial: 'glass',
        bonusAgainst: 'glass'
    }
}

export const DEFAULT_BOMBS = {
    basic: 1,
    explosive: 0,
    fire: 0,
    iron: 0
}

export const DEMO_BUILDS = [
    {
        title: 'Starter Stack',
        builderName: 'Codex',
        xHandle: '',
        flag: { x: 9.75, y: -5.025 },
        blocks: [
            { material: 'wood', shape: 'beam', x: 9.75, y: -5.7125, width: BLOCK_SHAPES.beam.width, height: BLOCK_SHAPES.beam.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 8.65, y: -5.025, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 10.85, y: -5.025, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'rectangle', x: 9.75, y: -4.2, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: 0 },
            { material: 'wood', shape: 'square', x: 9.75, y: -3.375, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 }
        ]
    },
    {
        title: 'Glass Crown',
        builderName: 'Vibe Builder',
        xHandle: '',
        flag: { x: 9.75, y: -5.3 },
        blocks: [
            { material: 'stone', shape: 'square', x: 8.65, y: -5.3, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 10.85, y: -5.3, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'wood', shape: 'rectangle', x: 9.75, y: -4.475, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: 0 },
            { material: 'glass', shape: 'beam', x: 9.75, y: -4.0625, width: BLOCK_SHAPES.beam.width, height: BLOCK_SHAPES.beam.height, rotation: 0 },
            { material: 'glass', shape: 'square', x: 8.65, y: -3.375, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'square', x: 10.85, y: -3.375, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 }
        ]
    },
    {
        title: 'The Citadel',
        builderName: 'Fortress Lab',
        xHandle: '',
        flag: { x: 10.2, y: -5.3 },
        blocks: [
            { material: 'wood', shape: 'rectangle', x: 8.825, y: -4.75, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: Math.PI / 2 },
            { material: 'wood', shape: 'rectangle', x: 11.575, y: -4.75, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: Math.PI / 2 },
            { material: 'wood', shape: 'beam', x: 10.2, y: -3.5125, width: BLOCK_SHAPES.beam.width, height: BLOCK_SHAPES.beam.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 9.1, y: -2.825, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 11.3, y: -2.825, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'rectangle', x: 10.2, y: -2, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: 0 },
            { material: 'glass', shape: 'square', x: 10.2, y: -1.175, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 6, y: -5.3, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'wood', shape: 'square', x: 6, y: -4.2, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'square', x: 6, y: -3.1, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 7.1, y: -5.3, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'wood', shape: 'square', x: 7.1, y: -4.2, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'square', x: 7.1, y: -3.1, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'wood', shape: 'rectangle', x: 6.55, y: -2.275, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 3.9, y: -5.3, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 3.9, y: -4.2, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'halfBrick', x: 3.9, y: -3.375, width: BLOCK_SHAPES.halfBrick.width, height: BLOCK_SHAPES.halfBrick.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 12.75, y: -5.3, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'wood', shape: 'square', x: 12.75, y: -4.2, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 }
        ]
    },
    {
        title: 'Glass Cathedral',
        builderName: 'Fortress Lab',
        xHandle: '',
        flag: { x: 9.6, y: -5.3 },
        blocks: [
            { material: 'stone', shape: 'square', x: 6.85, y: -5.3, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'square', x: 6.85, y: -4.2, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'square', x: 6.85, y: -3.1, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'square', x: 6.85, y: -2, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'halfBrick', x: 6.85, y: -1.175, width: BLOCK_SHAPES.halfBrick.width, height: BLOCK_SHAPES.halfBrick.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 12.35, y: -5.3, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'square', x: 12.35, y: -4.2, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'square', x: 12.35, y: -3.1, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'square', x: 12.35, y: -2, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'halfBrick', x: 12.35, y: -1.175, width: BLOCK_SHAPES.halfBrick.width, height: BLOCK_SHAPES.halfBrick.height, rotation: 0 },
            { material: 'glass', shape: 'rectangle', x: 8.755, y: -4.75, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: Math.PI / 2 },
            { material: 'glass', shape: 'rectangle', x: 10.445, y: -4.75, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: Math.PI / 2 },
            { material: 'wood', shape: 'beam', x: 9.6, y: -3.5125, width: BLOCK_SHAPES.beam.width, height: BLOCK_SHAPES.beam.height, rotation: 0 },
            { material: 'glass', shape: 'square', x: 9.6, y: -2.825, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'square', x: 9.6, y: -1.725, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'chip', x: 9.6, y: -0.9, width: BLOCK_SHAPES.chip.width, height: BLOCK_SHAPES.chip.height, rotation: 0 },
            { material: 'glass', shape: 'sliver', x: 5.6, y: -5.3, width: BLOCK_SHAPES.sliver.width, height: BLOCK_SHAPES.sliver.height, rotation: 0 },
            { material: 'glass', shape: 'sliver', x: 6.15, y: -5.3, width: BLOCK_SHAPES.sliver.width, height: BLOCK_SHAPES.sliver.height, rotation: 0 }
        ]
    },
    {
        title: 'Stone Bunker',
        builderName: 'Fortress Lab',
        xHandle: '',
        flag: { x: 9.5, y: -5.3 },
        blocks: [
            { material: 'stone', shape: 'square', x: 8.4, y: -5.3, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 7.3, y: -5.3, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 10.6, y: -5.3, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 11.7, y: -5.3, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'stone', shape: 'beam', x: 9.5, y: -4.6125, width: BLOCK_SHAPES.beam.width, height: BLOCK_SHAPES.beam.height, rotation: 0 },
            { material: 'stone', shape: 'rectangle', x: 8.4, y: -4.2, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: 0 },
            { material: 'stone', shape: 'rectangle', x: 10.6, y: -4.2, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 12.8, y: -5.3, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 12.8, y: -4.2, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'wood', shape: 'square', x: 12.8, y: -3.1, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'halfBrick', x: 7.85, y: -3.65, width: BLOCK_SHAPES.halfBrick.width, height: BLOCK_SHAPES.halfBrick.height, rotation: 0 },
            { material: 'glass', shape: 'halfBrick', x: 11.15, y: -3.65, width: BLOCK_SHAPES.halfBrick.width, height: BLOCK_SHAPES.halfBrick.height, rotation: 0 },
            { material: 'wood', shape: 'rectangle', x: 5.1, y: -5.575, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: 0 },
            { material: 'stone', shape: 'halfBrick', x: 5.65, y: -5.025, width: BLOCK_SHAPES.halfBrick.width, height: BLOCK_SHAPES.halfBrick.height, rotation: 0 },
            { material: 'wood', shape: 'halfBrick', x: 5.65, y: -4.475, width: BLOCK_SHAPES.halfBrick.width, height: BLOCK_SHAPES.halfBrick.height, rotation: 0 }
        ]
    },
    {
        title: 'Domino Gallery',
        builderName: 'Fortress Lab',
        xHandle: '',
        flag: { x: 11.1, y: -5.3 },
        blocks: [
            { material: 'wood', shape: 'rectangle', x: 4.2, y: -4.75, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: Math.PI / 2 },
            { material: 'wood', shape: 'rectangle', x: 5.05, y: -4.75, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: Math.PI / 2 },
            { material: 'wood', shape: 'rectangle', x: 5.9, y: -4.75, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: Math.PI / 2 },
            { material: 'wood', shape: 'rectangle', x: 6.75, y: -4.75, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: Math.PI / 2 },
            { material: 'wood', shape: 'rectangle', x: 7.6, y: -4.75, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: Math.PI / 2 },
            { material: 'wood', shape: 'rectangle', x: 8.45, y: -4.75, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: Math.PI / 2 },
            { material: 'stone', shape: 'rectangle', x: 9.725, y: -4.75, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: Math.PI / 2 },
            { material: 'stone', shape: 'rectangle', x: 12.475, y: -4.75, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: Math.PI / 2 },
            { material: 'wood', shape: 'beam', x: 11.1, y: -3.5125, width: BLOCK_SHAPES.beam.width, height: BLOCK_SHAPES.beam.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 11.1, y: -2.825, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'square', x: 11.1, y: -1.725, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 }
        ]
    },
    {
        title: 'Twin Towers',
        builderName: 'Fortress Lab',
        xHandle: '',
        flag: { x: 9.5, y: -5.3 },
        blocks: [
            { material: 'stone', shape: 'square', x: 7.6, y: -5.3, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 11.4, y: -5.3, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 7.6, y: -4.2, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 11.4, y: -4.2, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'wood', shape: 'square', x: 7.6, y: -3.1, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'wood', shape: 'square', x: 11.4, y: -3.1, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'wood', shape: 'square', x: 7.6, y: -2, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'wood', shape: 'square', x: 11.4, y: -2, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'wood', shape: 'rectangle', x: 8.655, y: -4.75, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: Math.PI / 2 },
            { material: 'wood', shape: 'rectangle', x: 10.345, y: -4.75, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: Math.PI / 2 },
            { material: 'glass', shape: 'rectangle', x: 9.5, y: -3.375, width: BLOCK_SHAPES.rectangle.width, height: BLOCK_SHAPES.rectangle.height, rotation: 0 },
            { material: 'wood', shape: 'beam', x: 9.5, y: -1.3125, width: BLOCK_SHAPES.beam.width, height: BLOCK_SHAPES.beam.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 8.4, y: -0.625, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 10.6, y: -0.625, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'glass', shape: 'square', x: 9.5, y: -0.625, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'wood', shape: 'beam', x: 9.5, y: 0.0625, width: BLOCK_SHAPES.beam.width, height: BLOCK_SHAPES.beam.height, rotation: 0 },
            { material: 'glass', shape: 'square', x: 9.5, y: 0.75, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 6.5, y: -5.3, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'wood', shape: 'halfBrick', x: 6.5, y: -4.475, width: BLOCK_SHAPES.halfBrick.width, height: BLOCK_SHAPES.halfBrick.height, rotation: 0 },
            { material: 'stone', shape: 'square', x: 12.5, y: -5.3, width: BLOCK_SHAPES.square.width, height: BLOCK_SHAPES.square.height, rotation: 0 },
            { material: 'wood', shape: 'halfBrick', x: 12.5, y: -4.475, width: BLOCK_SHAPES.halfBrick.width, height: BLOCK_SHAPES.halfBrick.height, rotation: 0 }
        ]
    }
]
